/**
 * Screen Slider
 * =============
 * Desktop : CSS position:sticky drives the full-height pinned slider.
 *           The wrapper is tall enough to provide scroll space for each slide
 *           (numSlides + 1) × 100vh). A passive scroll listener reads how far
 *           into the wrapper the user has scrolled and updates the active slide.
 *           No scroll hijacking / event.preventDefault needed.
 *           · Sections are opacity-stacked (position:absolute).
 *           · Each slide has its own static .media-border element (CSS only).
 *           · Background colour fades on .screen-slider-desktop[data-bg].
 * Mobile  : horizontal Slick swipe cards – no sticky behaviour.
 */
(function ($) {
  "use strict";

  /** Duration of the opacity cross-fade (ms) */
  const TRANSITION_DURATION = 500;

  /**
   * Fraction of the viewport height the user must scroll to advance one slide.
   * 1.0 = one full screen per slide (original).  0.5 = half a screen per slide.
   * Decrease to make slides feel snappier; increase for more deliberate pacing.
   */
  const SCROLL_PER_SLIDE = 0.5;

  /** Active ScreenSlider instances keyed by block-id */
  const instances = {};

  /** Current breakpoint – used to detect resize crossing lg */
  let currentBp = window.innerWidth >= 992 ? "desktop" : "mobile";

  // -------------------------------------------------------------------------
  // Utility
  // -------------------------------------------------------------------------
  function debounce(fn, ms) {
    let t;
    return function (...args) {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), ms);
    };
  }

  // =========================================================================
  // ScreenSlider class
  // =========================================================================
  class ScreenSlider {
    constructor($wrapper) {
      this.$wrapper = $wrapper;
      this.blockId = $wrapper.data("block-id");
      this.$desktop = $wrapper.find(".screen-slider-desktop");
      this.$sections = this.$desktop.find(".section");
      this.currentIndex = 0;
      this.isTransition = false;
      this.$nav = null;
      this.$status = null;
      this._transitionTimer = null;
      this._rafPending = false;
      // Cached layout measurements – set in _setWrapperHeight/_setWrapperTop
      // and used in the hot scroll path to avoid repeated getBoundingClientRect calls.
      this.wrapperTop = 0;
      this.wrapperHeight = 0;

      this._onScroll = this._handleScroll.bind(this);
      this._onResize = debounce(this._handleResize.bind(this), 150);

      this._init();
    }

    // -----------------------------------------------------------------------
    // Initialisation
    // -----------------------------------------------------------------------
    _init() {
      if (this.$sections.length < 1) return;

      this._buildNav();
      this._setWrapperHeight(); // sets this.wrapperHeight + min-height CSS
      this._setWrapperTop(); // sets this.wrapperTop (document offset)
      this._bindKeyboard();

      // Activate slide 0 immediately
      this.goToSection(0, false);

      // Sync with the current scroll position in case the page was
      // already scrolled into the block on load (after the opacity-in transition).
      setTimeout(() => {
        this._handleScroll();
      }, 320);

      window.addEventListener("scroll", this._onScroll, { passive: true });
      window.addEventListener("resize", this._onResize);
      this.$wrapper.addClass("initialized");
    }

    // -----------------------------------------------------------------------
    // Wrapper height – provides the scroll space for all slides.
    // 1 vh for the sticky panel itself + SCROLL_PER_SLIDE × vh per slide.
    //   e.g. SCROLL_PER_SLIDE=0.5, 3 slides → wrapper = 1 + 3×0.5 = 2.5 vh
    // -----------------------------------------------------------------------
    _setWrapperHeight() {
      const n  = this.$sections.length;
      const vh = window.innerHeight;
      this.wrapperHeight = Math.round(vh + n * SCROLL_PER_SLIDE * vh);
      this.$wrapper.css("min-height", this.wrapperHeight + "px");
    }

    // -----------------------------------------------------------------------
    // Cache the wrapper's absolute document top offset.
    // Must be called after _setWrapperHeight and after any layout change.
    // -----------------------------------------------------------------------
    _setWrapperTop() {
      this.wrapperTop =
        this.$wrapper[0].getBoundingClientRect().top + window.pageYOffset;
    }

    // -----------------------------------------------------------------------
    // Scroll handler – passive, rAF-throttled
    // -----------------------------------------------------------------------
    _handleScroll() {
      if (this._rafPending) return;
      this._rafPending = true;

      requestAnimationFrame(() => {
        this._rafPending = false;
        this._updateFromScroll();
      });
    }

    _updateFromScroll() {
      const scrollY = window.pageYOffset;
      const vh = window.innerHeight;
      const n = this.$sections.length;

      // How far the user has scrolled past the top edge of the wrapper.
      // Using window.pageYOffset (a stable integer) instead of
      // getBoundingClientRect avoids subpixel-rendering flicker at the
      // sticky entry/exit boundary.
      const scrolledIn = scrollY - this.wrapperTop;

      // Sticky zone: we've reached the wrapper top but haven't scrolled
      // past the point where the desktop panel would un-stick.
      const isSticky = scrolledIn >= 0 && scrolledIn <= this.wrapperHeight - vh;
      this.$wrapper.toggleClass("is-sticky", isSticky);

      if (!isSticky || n <= 1) return;

      // Each slide occupies SCROLL_PER_SLIDE × vh of scroll space
      const slideStep = SCROLL_PER_SLIDE * vh;
      const target = Math.min(Math.floor(scrolledIn / slideStep), n - 1);

      if (target !== this.currentIndex && !this.isTransition) {
        this.goToSection(target, true);
      }
    }

    // -----------------------------------------------------------------------
    // Scroll page so that the given slide index is the active one
    // -----------------------------------------------------------------------
    _scrollToSlide(index) {
      const vh = window.innerHeight;
      window.scrollTo({
        top: this.wrapperTop + index * SCROLL_PER_SLIDE * vh,
        behavior: "smooth",
      });
    }

    // -----------------------------------------------------------------------
    // Navigation dots
    // -----------------------------------------------------------------------
    _buildNav() {
      const $nav = $("<nav>", {
        class: "screen-slider-nav",
        role: "navigation",
        "aria-label": "Slide navigation",
      });
      const $ul = $("<ul>");

      this.$sections.each((i) => {
        $("<li>")
          .toggleClass("active", i === 0)
          .append(
            $("<button>", {
              "data-index": i,
              "aria-label": `Go to slide ${i + 1}`,
              "aria-current": i === 0 ? "true" : "false",
            }).append($("<span>")),
          )
          .appendTo($ul);
      });

      $nav.append($ul).appendTo(this.$wrapper);
      this.$nav = $nav;

      // Dot click → smooth-scroll page to the target slide position
      this.$nav.on("click", "button", (e) => {
        const idx = parseInt($(e.currentTarget).data("index"), 10);
        this._scrollToSlide(idx);
      });
    }

    // // -----------------------------------------------------------------------
    // // Accessibility helpers (skip link + live region)
    // // -----------------------------------------------------------------------
    // _buildA11y() {
    //   this.$wrapper.prepend(
    //     $("<a>", {
    //       class: "screen-slider-skip",
    //       href: "#after-screen-slider-" + this.blockId,
    //       tabindex: "0",
    //       text: "Skip slider",
    //     }),
    //   );

    //   this.$status = $("<div>", {
    //     class: "screen-slider-status",
    //     role: "status",
    //     "aria-live": "polite",
    //     "aria-atomic": "true",
    //   }).appendTo(this.$wrapper);

    //   $("<div>", {
    //     id: "after-screen-slider-" + this.blockId,
    //     tabindex: "-1",
    //   }).insertAfter(this.$wrapper);
    // }

    // -----------------------------------------------------------------------
    // Keyboard navigation (only active while the slider is sticky)
    // -----------------------------------------------------------------------
    _bindKeyboard() {
      $(document).on("keydown.screenslider-" + this.blockId, (e) => {
        const rect = this.$wrapper[0].getBoundingClientRect();
        const vh = window.innerHeight;
        const isSticky = rect.top <= 0 && rect.bottom >= vh;
        if (!isSticky) return;

        const map = {
          ArrowDown: 1,
          PageDown: 1,
          End: Infinity,
          ArrowUp: -1,
          PageUp: -1,
          Home: -Infinity,
        };
        if (!(e.key in map)) return;

        e.preventDefault();
        const delta = map[e.key];
        const next =
          delta === Infinity
            ? this.$sections.length - 1
            : delta === -Infinity
              ? 0
              : this.currentIndex + delta;
        this._scrollToSlide(
          Math.max(0, Math.min(next, this.$sections.length - 1)),
        );
      });
    }

    // -----------------------------------------------------------------------
    // Resize – recalculate scroll space
    // -----------------------------------------------------------------------
    _handleResize() {
      this._setWrapperHeight();
      this._setWrapperTop();
    }

    // -----------------------------------------------------------------------
    // Nav state
    // -----------------------------------------------------------------------
    _updateNav(index) {
      this.$nav.find("li").removeClass("active").eq(index).addClass("active");
      this.$nav
        .find("button")
        .attr("aria-current", "false")
        .eq(index)
        .attr("aria-current", "true");
    }

    _updateNavStyle($section) {
      const bg = $section.data("bg");
      const layout = $section.data("layout");

      this.$nav.toggleClass("dark-theme", bg === "light");

      const offset = this._navEdgeOffset();
      if (layout === "image-right") {
        this.$nav.css({ right: "auto", left: offset });
      } else {
        this.$nav.css({ right: offset, left: "auto" });
      }
    }

    // Returns the pixel offset from the viewport edge that keeps the nav
    // 40 px inside the site wrapper, even on viewports wider than 1650 px.
    _navEdgeOffset() {
      const vw      = window.innerWidth;
      const maxW    = 1650;
      const inset   = 40;
      const surplus = vw > maxW ? Math.round((vw - maxW) / 2) : 0;
      return (surplus + inset) + "px";
    }

    // -----------------------------------------------------------------------
    // Core: activate a slide
    // -----------------------------------------------------------------------
    goToSection(index, animate) {
      if (index < 0 || index >= this.$sections.length) return;
      if (animate && index === this.currentIndex) return;

      // Cancel any in-flight transition cleanup timer
      if (this._transitionTimer) {
        clearTimeout(this._transitionTimer);
        this._transitionTimer = null;
        this.isTransition = false;
      }

      const prev = this.currentIndex;
      this.currentIndex = index;

      const $prev = this.$sections.eq(prev);
      const $next = this.$sections.eq(index);

      this._updateNav(index);
      this._updateNavStyle($next);
      if (this.$status) {
        this.$status.text(`Slide ${index + 1} of ${this.$sections.length}`);
      }

      this.$desktop.attr("data-bg", $next.data("bg"));

      if (!animate) {
        this.$sections.removeClass("is-active is-leaving");
        $next.addClass("is-active");
        return;
      }

      this.isTransition = true;

      $prev.removeClass("is-active").addClass("is-leaving");
      $next.addClass("is-active");

      this._transitionTimer = setTimeout(() => {
        $prev.removeClass("is-leaving");
        this.isTransition = false;
        this._transitionTimer = null;
      }, TRANSITION_DURATION + 60);
    }

    // -----------------------------------------------------------------------
    // Cleanup
    // -----------------------------------------------------------------------
    destroy() {
      if (this._transitionTimer) clearTimeout(this._transitionTimer);
      window.removeEventListener("scroll", this._onScroll);
      window.removeEventListener("resize", this._onResize);
      $(document).off("keydown.screenslider-" + this.blockId);
      if (this.$nav) this.$nav.remove();
      this.$wrapper.find(".screen-slider-skip, .screen-slider-status").remove();
      $("#after-screen-slider-" + this.blockId).remove();
      this.$wrapper.css("min-height", "").removeClass("initialized is-sticky");
    }
  }

  // =========================================================================
  // Mobile Slick slider helpers
  // =========================================================================
  function initMobileSlider(blockId) {
    const $slider = $("#" + blockId).find(".screen-slider-mobile");
    if (!$slider.length) return;
    if ($slider.hasClass("slick-initialized")) $slider.slick("unslick");

    $slider.slick({
      dots: true,
      arrows: false,
      infinite: false,
      slidesToShow: 1,
      slidesToScroll: 1,
      adaptiveHeight: false,
      swipeToSlide: true,
      speed: 600,
      cssEase: "cubic-bezier(0.4, 0.0, 0.2, 1)",
      accessibility: true,
    });
  }

  function destroyMobileSlider(blockId) {
    const $slider = $("#" + blockId).find(".screen-slider-mobile");
    if ($slider.hasClass("slick-initialized")) $slider.slick("unslick");
  }

  // =========================================================================
  // Bootstrap / teardown based on viewport width
  // =========================================================================
  function syncSliders() {
    $(".screen-slider-wrapper").each(function () {
      const blockId = $(this).data("block-id");

      if (window.innerWidth >= 992) {
        destroyMobileSlider(blockId);
        if (!instances[blockId]) {
          instances[blockId] = new ScreenSlider($(this));
        }
      } else {
        if (instances[blockId]) {
          instances[blockId].destroy();
          delete instances[blockId];
        }
        initMobileSlider(blockId);
      }
    });
  }

  const onResize = debounce(() => {
    const bp = window.innerWidth >= 992 ? "desktop" : "mobile";
    if (bp !== currentBp) {
      currentBp = bp;
      syncSliders();
    }
  }, 250);

  // =========================================================================
  // Boot
  // =========================================================================
  $(document).ready(() => {
    if ($(".screen-slider-wrapper").length) {
      syncSliders();
      window.addEventListener("resize", onResize);
    }
  });
})(jQuery);
